import { useState, useEffect, useCallback } from 'react';
import type { SiteConfig } from '@bosamie/shared';
import { loadCatalog, type Catalog } from '../api/client';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T>(fetcher: () => Promise<T>, deps: any[] = []): ApiState<T> & { refetch: () => void } {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const data = await fetcher();
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: err instanceof Error ? err.message : 'Unknown error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { ...state, refetch: fetchData };
}

/**
 * 全站共享一份目录。
 *
 * 为什么做成 hook 而不是各页面各自请求：
 *   三个页面（首页/品牌/型号）读的是同一份数据，各自请求会重复拉 72KB；
 *   `loadCatalog` 内部已有模块级缓存与在途去重，这里只把结果接到 React 状态上。
 */
export function useCatalog(): ApiState<Catalog> & { refetch: () => void } {
  const [state, setState] = useState<ApiState<Catalog>>({
    data: null,
    loading: true,
    error: null,
  });
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    // 保留上一次的数据：刷新时不要闪白，页面可以继续显示旧内容
    setState((prev) => ({ data: prev.data, loading: true, error: null }));

    loadCatalog(reloadTick > 0)
      .then((data) => {
        if (alive) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (alive) {
          setState({ data: null, loading: false, error: err instanceof Error ? err.message : 'Load failed' });
        }
      });

    return () => {
      alive = false;
    };
  }, [reloadTick]);

  return { ...state, refetch: () => setReloadTick((n) => n + 1) };
}

/** 联系方式等站点配置，统一从目录快照里取（不再单独请求 /public/config）。 */
export function useConfig(): SiteConfig | null {
  const { data } = useCatalog();
  return data?.site ?? null;
}

// Toast state management
let toastTimer: ReturnType<typeof setTimeout> | null = null;
let toastEl: HTMLElement | null = null;

export function showToast(msg: string) {
  if (toastTimer) clearTimeout(toastTimer);

  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    document.body.appendChild(toastEl);
  }

  toastEl.textContent = msg;
  toastEl.style.display = 'block';
  toastEl.style.animation = 'none';
  toastEl.offsetHeight; // force reflow
  toastEl.style.animation = 'toast-in 0.25s, toast-out 0.25s 1.5s forwards';

  toastTimer = setTimeout(() => {
    if (toastEl) toastEl.style.display = 'none';
  }, 1800);
}
